
import { Goal } from "@/types/todo";
import { toast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import React from 'react';

// Generate a stable notification ID based on the item and its deadline
// This ensures the same deadline gets the same ID across sessions
const generateNotificationId = (type: 'goal' | 'todo', itemId: string, dueDate: string) => {
  return `${type}_deadline_${itemId}_${dueDate}`;
};

// Track toasts shown in current session to prevent duplicates within a session
const sessionToastIds = new Set<string>();

export const checkDeadlines = (
  goals: Goal[], 
  hasAcknowledged: (id: string) => boolean,
  acknowledgeNotification?: (id: string, deadlineDate?: string) => void
) => {
  const notifications: Array<{
    id: string;
    title: string;
    description: string;
    deadlineDate: string;
  }> = [];
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  goals.forEach(goal => {
    if (!goal.dueDate) return;
    
    const deadline = new Date(goal.dueDate);
    deadline.setHours(0, 0, 0, 0);
    const daysUntilDeadline = Math.ceil((deadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    // Use stable ID based on goal ID and due date
    const goalNotificationId = generateNotificationId('goal', goal.id, goal.dueDate);

    // Check if already acknowledged OR already shown this session
    if (!hasAcknowledged(goalNotificationId) && !sessionToastIds.has(goalNotificationId)) {
      if (daysUntilDeadline <= 7 && daysUntilDeadline > 0) {
        notifications.push({
          id: goalNotificationId,
          title: "Upcoming Deadline",
          description: `${goal.title} is due in ${daysUntilDeadline} day${daysUntilDeadline > 1 ? 's' : ''}!`,
          deadlineDate: goal.dueDate
        });
      } else if (daysUntilDeadline === 0) {
        notifications.push({
          id: goalNotificationId,
          title: "Goal Due Today!",
          description: `${goal.title} is due today!`,
          deadlineDate: goal.dueDate
        });
      }
    }

    // Check todos within goals
    goal.todos?.forEach(todo => {
      if (!todo.completed && todo.dueDate) {
        const todoDueDate = new Date(todo.dueDate);
        todoDueDate.setHours(0, 0, 0, 0);
        const daysUntilTodo = Math.ceil((todoDueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        
        const todoNotificationId = generateNotificationId('todo', todo.id, todo.dueDate);

        if (!hasAcknowledged(todoNotificationId) && !sessionToastIds.has(todoNotificationId)) {
          if (daysUntilTodo <= 3 && daysUntilTodo > 0) {
            notifications.push({
              id: todoNotificationId,
              title: "Upcoming Todo",
              description: `Todo "${todo.text}" for "${goal.title}" is due in ${daysUntilTodo} day${daysUntilTodo > 1 ? 's' : ''}!`,
              deadlineDate: todo.dueDate
            });
          } else if (daysUntilTodo === 0) {
            notifications.push({
              id: todoNotificationId,
              title: "Todo Due Today!",
              description: `Todo "${todo.text}" for "${goal.title}" is due today!`,
              deadlineDate: todo.dueDate
            });
          }
        }
      }
    });
  });
  
  // Show notifications
  notifications.forEach(notification => {
    // Mark as shown in this session immediately
    sessionToastIds.add(notification.id);
    
    toast({
      title: notification.title,
      description: notification.description,
      duration: 8000,
      action: (
        <ToastAction 
          altText={`Acknowledge ${notification.title}`}
          onClick={() => {
            // Acknowledge with the deadline date so it persists until deadline passes
            if (acknowledgeNotification) {
              acknowledgeNotification(notification.id, notification.deadlineDate);
            }
            return true;
          }}
        >
          Acknowledge
        </ToastAction>
      )
    });
  });
};

// Clear session tracking (call on logout or major state reset)
export const clearSessionNotifications = () => {
  sessionToastIds.clear();
};
